import React, { useState, useEffect } from "react";
import { Table, Pagination, Form, InputGroup, Button, Modal, Alert, Row, Col } from "react-bootstrap";
import apiClient from "../../services/apiClient";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [modalMode, setModalMode] = useState("Create");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch users whenever filter changes or after CRUD operations
  const fetchUsers = async () => {
    try {
      setLoading(true);
      let url = "/api/user/GetAll";
      if (filterRole !== "All") {
        url = `/api/user/role/${filterRole}`;
      }
      const response = await apiClient.get(url);
      setUsers(response.data);
    } catch (error) {
      setError("Failed to fetch users: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filterRole]);

  // Reset form error when modal is closed
  useEffect(() => {
    if (!showModal) {
      setError("");
    }
  }, [showModal]);

  // Filter users based on search query
  const filteredUsers = users.filter((user) => {
    const searchString = `${user.firstName || ""} ${user.lastName || ""} ${user.email} ${user.userCode || ""}`
      .toLowerCase();
    return searchString.includes(searchQuery.toLowerCase());
  });

  // Pagination setup
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await apiClient.delete(`/api/user/${userId}`);
        await fetchUsers(); // Refresh the list
        setError("");
      } catch (error) {
        setError("Failed to delete user: " + (error.response?.data?.message || error.message));
      }
    }
  };

  const validatePhoneNumber = (phone) => {
    // Basic phone validation: allows +, spaces, dashes, and numbers
    // Minimum 10 digits (not counting special characters)
    const phoneRegex = /^\+?[\d\s-]{10,}$/;
    return !phone || phoneRegex.test(phone);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    // Client-side validation
    if (currentUser?.phoneNumber && !validatePhoneNumber(currentUser.phoneNumber)) {
      setError("Please enter a valid phone number or leave it empty");
      return;
    }

    try {
      // Process data before sending
      const userData = Object.entries(currentUser || {}).reduce((acc, [key, value]) => {
        // Skip empty values
        if (value === "" || value === null || value === undefined) {
          return acc;
        }
        
        // Handle birth date conversion to UTC
        if (key === 'birthDate' && value) {
          // Create date object and ensure it's UTC
          const date = new Date(value);
          date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
          acc[key] = date.toISOString();
        } else {
          acc[key] = value;
        }
        
        return acc;
      }, {});

      if (modalMode === "Create") {
        await apiClient.post("/api/user/register", userData);
      } else {
        if (!userData.password) {
          delete userData.password;
        }
        await apiClient.put(`/api/user/${currentUser.id}`, userData);
      }
      
      await fetchUsers(); // Refresh the list
      setShowModal(false);
      setCurrentUser(null);
    } catch (error) {
      const errorMessage = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(", ")
        : error.response?.data?.message || error.message;
      setError(`Failed to ${modalMode.toLowerCase()} user: ${errorMessage}`);
    }
  };

  const handleCreate = () => {
    setCurrentUser({
      username: "",
      email: "",
      password: "",
      role: "",
      firstName: "",
      lastName: "",
      birthDate: "",
      gender: "",
      bloodType: "",
      phoneNumber: "",
      postcode: "",
      address: ""
    });
    setModalMode("Create");
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setCurrentUser({
      ...user,
      password: "", // Clear password field for editing
      birthDate: user.birthDate ? user.birthDate.split('T')[0] : "" // Format date for input
    });
    setModalMode("Edit");
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="p-4">
      <h1 className="mb-4">User Management</h1>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <div className="d-flex gap-3 mb-4">
        <InputGroup>
          <Form.Control
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Button variant="secondary" onClick={() => setSearchQuery("")}>
            Clear
          </Button>
        </InputGroup>

        <Form.Select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          style={{ width: 'auto' }}
        >
          <option value="All">All Roles</option>
          <option value="Admin">Admin</option>
          <option value="Doctor">Doctor</option>
          <option value="Patient">Patient</option>
          <option value="Radiologist">Radiologist</option>
        </Form.Select>

        <Button onClick={handleCreate} variant="success">
          Add User
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-4">Loading...</div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>#</th>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>User Code</th>
              <th>Name</th>
              <th>Birth Date</th>
              <th>Contact</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user, index) => (
              <tr key={user.id}>
                <td>{indexOfFirstItem + index + 1}</td>
                <td>{user.username}</td>
                <td>{user.email}</td>
                <td>{user.role}</td>
                <td>{user.userCode || "-"}</td>
                <td>{`${user.firstName || ""} ${user.lastName || ""}`.trim() || "-"}</td>
                <td>{user.birthDate ? new Date(user.birthDate).toLocaleDateString() : "-"}</td>
                <td>{user.phoneNumber || "-"}</td>
                <td>
                  <Button variant="warning" size="sm" className="me-2" onClick={() => handleEdit(user)}>
                    Edit
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(user.id)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Pagination className="justify-content-center">
        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1} />
        {Array.from({ length: totalPages }, (_, i) => (
          <Pagination.Item
            key={i + 1}
            active={currentPage === i + 1}
            onClick={() => setCurrentPage(i + 1)}
          >
            {i + 1}
          </Pagination.Item>
        ))}
        <Pagination.Next
          onClick={() => setCurrentPage(prev => prev + 1)}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        />
      </Pagination>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalMode} User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Username*</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={currentUser?.username || ""}
                    onChange={handleInputChange}
                    required
                    readOnly={modalMode === "Edit"}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email*</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={currentUser?.email || ""}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role*</Form.Label>
                  <Form.Select
                    name="role"
                    value={currentUser?.role || ""}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Role</option>
                    <option value="Admin">Admin</option>
                    <option value="Doctor">Doctor</option>
                    <option value="Patient">Patient</option>
                    <option value="Radiologist">Radiologist</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{modalMode === "Create" ? "Password*" : "New Password"}</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={currentUser?.password || ""}
                    onChange={handleInputChange}
                    required={modalMode === "Create"}
                    minLength={6}
                  />
                  {modalMode === "Edit" && (
                    <Form.Text className="text-muted">
                      Leave empty to keep current password
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={currentUser?.firstName || ""}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={currentUser?.lastName || ""}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Birth Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="birthDate"
                    value={currentUser?.birthDate || ""}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number (Optional)</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phoneNumber"
                    value={currentUser?.phoneNumber || ""}
                    onChange={handleInputChange}
                    isInvalid={currentUser?.phoneNumber && !validatePhoneNumber(currentUser.phoneNumber)}
                    placeholder="e.g., +1234567890"
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter a valid phone number with at least 10 digits or leave it empty
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                   
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {modalMode === "Create" ? "Create" : "Save Changes"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default UserManagement;